import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ChevronDown, Pencil, Plus, Trash2 } from "lucide-react";

import { apiFetch, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { HubTouchSelect } from "@/components/ui/HubTouchSelect";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { PageTitleWithHelp, CardTitleWithHelp } from "@/components/ui/title-help";
import { NAV_BY_APP, type NavItem } from "@/lib/appNavigation";
import {
  applyHomeDashboardTemplate,
  createHomeDashboardTemplate,
  fetchHomeDashboardCatalog,
  fetchHomeDashboardLayout,
  fetchHomeDashboardTemplates,
  fetchHomeDashboardWidgets,
  saveHomeDashboardLayout,
  updateHomeDashboardTemplate,
  type HomeDashboardCatalogWidget,
  type HomeDashboardColumnSpan,
  type HomeDashboardLayoutWidget,
  type HomeDashboardPresentation,
  type HomeDashboardRowSpan,
  type HomeDashboardWidgetKey,
  type HomeDashboardWidgetPayload,
} from "@/lib/homeDashboardApi";
import { useIntegratorMe } from "@/lib/me-query";
import { allowedModuleKeysFromMe } from "@/lib/subapp-access";
import { formatHubSupportTicketStatusLabel } from "@/lib/hubSupportTicketStatus";
import { HUB_SUPPORT_TICKET_STATUS_META } from "@/lib/hubSupportTicketStatusMeta";
import { useNavigationTrail } from "@/lib/navigationTrailContext";
import { FLOATING_POPOVER_Z_INDEX } from "@/lib/viewportAnchoredPopover";
import { cn } from "@/lib/utils";

const LAYOUT_QUERY_KEY = ["home-dashboard", "layout"] as const;
const WIDGETS_QUERY_KEY = ["home-dashboard", "widgets"] as const;
const CATALOG_QUERY_KEY = ["home-dashboard", "catalog"] as const;
const TEMPLATES_QUERY_KEY = ["home-dashboard", "templates"] as const;
const GRID_COLUMNS = 4 as const;
const MAX_ROW_SPAN = 8 as const;
const SLOT_ROW_HEIGHT = "10.5rem";
const GRID_GAP_PX = 16;
const EDIT_EMPTY_MINIMUM_ROWS = 4;

function formatCount(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) {
    return "—";
  }
  return Math.trunc(value).toLocaleString("en-US");
}

function formatWidgetCount(widgetKey: string, value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) {
    return "—";
  }
  if (widgetKey === "ap_invoices_due_next_30_days") {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value);
  }
  return formatCount(value);
}

function normalizeColumnSpan(raw: unknown): HomeDashboardColumnSpan {
  const value =
    typeof raw === "number" && Number.isFinite(raw)
      ? Math.trunc(raw)
      : typeof raw === "string" && raw.trim() !== "" && Number.isFinite(Number(raw))
        ? Math.trunc(Number(raw))
        : 1;
  if (value <= 1) {
    return 1;
  }
  if (value >= GRID_COLUMNS) {
    return GRID_COLUMNS;
  }
  return value as HomeDashboardColumnSpan;
}

function normalizeRowSpan(raw: unknown): HomeDashboardRowSpan {
  const value =
    typeof raw === "number" && Number.isFinite(raw)
      ? Math.trunc(raw)
      : typeof raw === "string" && raw.trim() !== "" && Number.isFinite(Number(raw))
        ? Math.trunc(Number(raw))
        : 1;
  if (value <= 1) {
    return 1;
  }
  if (value >= MAX_ROW_SPAN) {
    return MAX_ROW_SPAN;
  }
  return value as HomeDashboardRowSpan;
}

function normalizeCol(raw: unknown): number {
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return Math.max(0, Math.min(GRID_COLUMNS - 1, Math.trunc(raw)));
  }
  return 0;
}

function normalizeRow(raw: unknown): number {
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return Math.max(0, Math.trunc(raw));
  }
  return 0;
}

function widgetsOverlap(
  left: Pick<HomeDashboardLayoutWidget, "col" | "row" | "columnSpan" | "rowSpan">,
  right: Pick<HomeDashboardLayoutWidget, "col" | "row" | "columnSpan" | "rowSpan">,
): boolean {
  const leftCol = normalizeCol(left.col);
  const leftRow = normalizeRow(left.row);
  const rightCol = normalizeCol(right.col);
  const rightRow = normalizeRow(right.row);
  const leftColumnSpan = normalizeColumnSpan(left.columnSpan);
  const leftRowSpan = normalizeRowSpan(left.rowSpan);
  const rightColumnSpan = normalizeColumnSpan(right.columnSpan);
  const rightRowSpan = normalizeRowSpan(right.rowSpan);
  return (
    leftCol < rightCol + rightColumnSpan &&
    leftCol + leftColumnSpan > rightCol &&
    leftRow < rightRow + rightRowSpan &&
    leftRow + leftRowSpan > rightRow
  );
}

function placementFits(
  widgets: HomeDashboardLayoutWidget[],
  candidate: {
    instanceId: string;
    col: number;
    row: number;
    columnSpan: HomeDashboardColumnSpan;
    rowSpan: HomeDashboardRowSpan;
  },
): boolean {
  if (candidate.col < 0 || candidate.row < 0) {
    return false;
  }
  if (candidate.col + candidate.columnSpan > GRID_COLUMNS) {
    return false;
  }
  for (const widget of widgets) {
    if (widget.instanceId === candidate.instanceId) {
      continue;
    }
    if (
      widgetsOverlap(candidate, {
        col: normalizeCol(widget.col),
        row: normalizeRow(widget.row),
        columnSpan: normalizeColumnSpan(widget.columnSpan),
        rowSpan: normalizeRowSpan(widget.rowSpan),
      })
    ) {
      return false;
    }
  }
  return true;
}

function findFirstOpenSlot(
  widgets: HomeDashboardLayoutWidget[],
  columnSpan: HomeDashboardColumnSpan,
  rowSpan: HomeDashboardRowSpan,
): { col: number; row: number } {
  for (let row = 0; row < 48; row += 1) {
    for (let col = 0; col <= GRID_COLUMNS - columnSpan; col += 1) {
      if (
        placementFits(widgets, {
          instanceId: "__new__",
          col,
          row,
          columnSpan,
          rowSpan,
        })
      ) {
        return { col, row };
      }
    }
  }
  return { col: 0, row: 0 };
}

function widgetOccupiesCell(widget: HomeDashboardLayoutWidget, col: number, row: number): boolean {
  const widgetCol = normalizeCol(widget.col);
  const widgetRow = normalizeRow(widget.row);
  const columnSpan = normalizeColumnSpan(widget.columnSpan);
  const rowSpan = normalizeRowSpan(widget.rowSpan);
  return col >= widgetCol && col < widgetCol + columnSpan && row >= widgetRow && row < widgetRow + rowSpan;
}

function listEmptySlots(
  widgets: HomeDashboardLayoutWidget[],
  extraRows: number,
  minimumRows: number,
): { col: number; row: number }[] {
  let maxRow = 0;
  for (const widget of widgets) {
    maxRow = Math.max(maxRow, normalizeRow(widget.row) + normalizeRowSpan(widget.rowSpan));
  }
  const rowCount = Math.max(minimumRows, maxRow + Math.max(0, extraRows));
  const empty: { col: number; row: number }[] = [];
  for (let row = 0; row < rowCount; row += 1) {
    for (let col = 0; col < GRID_COLUMNS; col += 1) {
      if (!widgets.some((widget) => widgetOccupiesCell(widget, col, row))) {
        empty.push({ col, row });
      }
    }
  }
  return empty;
}

function placeWidgetPushingDownLocal(
  widgets: HomeDashboardLayoutWidget[],
  instanceId: string,
  placement: {
    col: number;
    row: number;
    columnSpan: HomeDashboardColumnSpan;
    rowSpan: HomeDashboardRowSpan;
  },
): HomeDashboardLayoutWidget[] | null {
  if (placement.col < 0 || placement.row < 0) {
    return null;
  }
  if (placement.col + placement.columnSpan > GRID_COLUMNS) {
    return null;
  }

  let next = widgets.map((widget) =>
    widget.instanceId === instanceId
      ? {
          ...widget,
          col: placement.col,
          row: placement.row,
          columnSpan: placement.columnSpan,
          rowSpan: placement.rowSpan,
        }
      : { ...widget },
  );

  for (let guard = 0; guard < 200; guard += 1) {
    let leftOverlap: HomeDashboardLayoutWidget | null = null;
    let rightOverlap: HomeDashboardLayoutWidget | null = null;
    for (let index = 0; index < next.length; index += 1) {
      for (let otherIndex = index + 1; otherIndex < next.length; otherIndex += 1) {
        if (
          widgetsOverlap(
            {
              col: normalizeCol(next[index]!.col),
              row: normalizeRow(next[index]!.row),
              columnSpan: normalizeColumnSpan(next[index]!.columnSpan),
              rowSpan: normalizeRowSpan(next[index]!.rowSpan),
            },
            {
              col: normalizeCol(next[otherIndex]!.col),
              row: normalizeRow(next[otherIndex]!.row),
              columnSpan: normalizeColumnSpan(next[otherIndex]!.columnSpan),
              rowSpan: normalizeRowSpan(next[otherIndex]!.rowSpan),
            },
          )
        ) {
          leftOverlap = next[index]!;
          rightOverlap = next[otherIndex]!;
          break;
        }
      }
      if (leftOverlap != null) {
        break;
      }
    }
    if (leftOverlap == null || rightOverlap == null) {
      return next.map((widget, index) => ({ ...widget, order: index }));
    }

    let stay = leftOverlap;
    let push = rightOverlap;
    if (leftOverlap.instanceId === instanceId) {
      stay = leftOverlap;
      push = rightOverlap;
    } else if (rightOverlap.instanceId === instanceId) {
      stay = rightOverlap;
      push = leftOverlap;
    } else if (
      normalizeRow(rightOverlap.row) < normalizeRow(leftOverlap.row) ||
      (normalizeRow(rightOverlap.row) === normalizeRow(leftOverlap.row) &&
        normalizeCol(rightOverlap.col) < normalizeCol(leftOverlap.col))
    ) {
      stay = rightOverlap;
      push = leftOverlap;
    }

    const newRow = normalizeRow(stay.row) + normalizeRowSpan(stay.rowSpan);
    next = next.map((widget) =>
      widget.instanceId === push.instanceId ? { ...widget, row: newRow } : widget,
    );
  }
  return null;
}

function spanFromPointerDelta(
  startSpan: number,
  pointerDelta: number,
  slotSize: number,
  maxSpan: number,
): number {
  const next = Math.round(startSpan + pointerDelta / Math.max(1, slotSize));
  return Math.max(1, Math.min(maxSpan, next));
}

function newInstanceId(): string {
  return `w_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function presentationLabel(value: HomeDashboardPresentation): string {
  switch (value) {
    case "count":
      return "Count";
    case "list":
      return "List";
    case "chart":
      return "Breakdown chart";
    case "links":
      return "Links";
    default:
      return value;
  }
}

function BarChart({ series }: { series: { label: string; value: number }[] }) {
  const max = Math.max(1, ...series.map((point) => point.value));
  if (series.length === 0) {
    return <p className="text-sm text-slate-500">No breakdown data.</p>;
  }
  return (
    <ul className="space-y-2">
      {series.map((point) => (
        <li key={point.label} className="space-y-1">
          <div className="flex items-baseline justify-between gap-2 text-sm">
            <span className="truncate text-slate-700">{point.label}</span>
            <span className="shrink-0 font-medium tabular-nums text-slate-900">{point.value}</span>
          </div>
          <div className="h-2 overflow-hidden rounded bg-slate-100">
            <div
              className="h-full rounded bg-brand-600"
              style={{ width: `${Math.max(4, Math.round((point.value / max) * 100))}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

function WidgetPreview({
  entry,
  presentation,
}: {
  entry: HomeDashboardCatalogWidget;
  presentation: HomeDashboardPresentation;
}) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
      <p className="text-sm font-medium text-slate-900">{entry.title}</p>
      <p className="mt-1 text-xs text-slate-600">{entry.description}</p>
      <div className="mt-4 rounded-md border border-slate-200 bg-white p-3">
        {presentation === "count" ? (
          <p className="text-3xl font-semibold tabular-nums text-slate-900">12</p>
        ) : presentation === "list" ? (
          <ul className="space-y-2 text-sm">
            <li>
              <span className="font-medium text-brand-700">Example item A</span>
              <span className="ml-2 text-slate-500">detail</span>
            </li>
            <li>
              <span className="font-medium text-brand-700">Example item B</span>
              <span className="ml-2 text-slate-500">detail</span>
            </li>
          </ul>
        ) : presentation === "chart" ? (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Total <span className="font-semibold tabular-nums text-slate-900">9</span>
            </p>
            <BarChart
              series={[
                { label: "sgs", value: 5 },
                { label: "suntronic", value: 3 },
                { label: "kodiak", value: 1 },
              ]}
            />
          </div>
        ) : (
          <ul className="space-y-2 text-sm">
            <li className="font-medium text-brand-700">Forecast</li>
            <li className="font-medium text-brand-700">Incoming orders</li>
            <li className="font-medium text-brand-700">Paperwork</li>
          </ul>
        )}
      </div>
      <p className="mt-3 text-xs text-slate-500">Preview uses sample numbers. Live counts load after you add the widget.</p>
    </div>
  );
}

type SupportTicketWidgetOptions = {
  ticketStatuses: string[];
  ticketQueue: "active" | "archived" | "any";
  ticketPendingFeedback: boolean;
};

function SupportTicketWidgetOptionsFields({
  idPrefix,
  value,
  onChange,
}: {
  idPrefix: string;
  value: SupportTicketWidgetOptions;
  onChange: (next: SupportTicketWidgetOptions) => void;
}) {
  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-1" role="group" aria-label="Ticket queue">
        {(
          [
            ["active", "Active queue"],
            ["archived", "Archived"],
            ["any", "Active and archived"],
          ] as const
        ).map(([queue, label]) => (
          <button
            key={queue}
            type="button"
            className={cn(
              "rounded px-2 py-1 text-left text-sm hover:bg-slate-50",
              value.ticketQueue === queue ? "font-medium text-brand-700" : "text-slate-800",
            )}
            onClick={() => onChange({ ...value, ticketQueue: queue })}
          >
            {label}
          </button>
        ))}
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-800">
        <input
          id={`${idPrefix}-pending`}
          type="checkbox"
          checked={value.ticketPendingFeedback}
          onChange={() => onChange({ ...value, ticketPendingFeedback: !value.ticketPendingFeedback })}
        />
        Pending feedback only
      </label>
      <fieldset className="space-y-2">
        <legend className="text-sm font-medium text-slate-800">Statuses</legend>
        <p className="text-xs text-slate-500">Leave all unchecked to include every status.</p>
        <div className="grid grid-cols-2 gap-2">
          {HUB_SUPPORT_TICKET_STATUS_META.map((row) => {
            const checked = value.ticketStatuses.includes(row.status);
            return (
              <label key={row.status} className="flex items-center gap-2 text-sm text-slate-800">
                <input
                  id={`${idPrefix}-${row.status}`}
                  type="checkbox"
                  checked={checked}
                  onChange={() => {
                    onChange({
                      ...value,
                      ticketStatuses: checked
                        ? value.ticketStatuses.filter((status) => status !== row.status)
                        : [...value.ticketStatuses, row.status],
                    });
                  }}
                />
                <span className="capitalize">{formatHubSupportTicketStatusLabel(row.status)}</span>
              </label>
            );
          })}
        </div>
      </fieldset>
    </div>
  );
}

function AddWidgetModal({
  open,
  catalog,
  busy,
  onClose,
  onAdd,
}: {
  open: boolean;
  catalog: HomeDashboardCatalogWidget[];
  busy: boolean;
  onClose: () => void;
  onAdd: (
    widgetKey: HomeDashboardWidgetKey,
    presentation: HomeDashboardPresentation,
    ticketOptions: SupportTicketWidgetOptions,
  ) => void;
}) {
  const [widgetKey, setWidgetKey] = useState("");
  const [presentation, setPresentation] = useState<HomeDashboardPresentation>("count");
  const [ticketOptions, setTicketOptions] = useState<SupportTicketWidgetOptions>({
    ticketStatuses: [],
    ticketQueue: "active",
    ticketPendingFeedback: false,
  });
  const [requestText, setRequestText] = useState("");
  const [requestError, setRequestError] = useState<string | null>(null);
  const [requestBusy, setRequestBusy] = useState(false);
  const [requestSentId, setRequestSentId] = useState<string | null>(null);
  const [requesting, setRequesting] = useState(false);
  const [widgetSearch, setWidgetSearch] = useState("");
  const wasOpen = useRef(false);
  const { resetNavigationTrail } = useNavigationTrail();
  const entry = catalog.find((candidate) => candidate.key === widgetKey) ?? null;

  useEffect(() => {
    if (open && !wasOpen.current) {
      setWidgetKey("");
      setPresentation("count");
      setTicketOptions({
        ticketStatuses: [],
        ticketQueue: "active",
        ticketPendingFeedback: false,
      });
      setRequestText("");
      setRequestError(null);
      setRequestSentId(null);
      setRequesting(false);
      setWidgetSearch("");
    }
    wasOpen.current = open;
  }, [open]);

  async function submitWidgetRequest(): Promise<void> {
    const description = requestText.trim();
    if (description === "") {
      setRequestError("Describe the widget you want.");
      return;
    }
    setRequestError(null);
    setRequestBusy(true);
    try {
      const result = await apiFetch<{ ok: boolean; id: string | null }>("/api/hub-support-tickets", {
        method: "POST",
        body: {
          kind: "suggestion",
          actualBehavior: `Dashboard widget request:\n${description}`,
          expectedBehavior: "Add a home dashboard widget that matches this request.",
          pagePath: "/personal/home",
          pageUrlFull: window.location.href,
          userAgent: navigator.userAgent,
        },
      });
      setRequestSentId(result.id);
    } catch (caught) {
      setRequestError(caught instanceof ApiError ? caught.message : "Could not open a support ticket.");
    } finally {
      setRequestBusy(false);
    }
  }

  if (!open) {
    return null;
  }

  return createPortal(
    <div {...hubModalOverlayProps(true)}>
      <Card className="my-8 w-full max-w-lg shadow-xl" onClick={(event) => event.stopPropagation()}>
        <CardHeader>
          <h2 className="text-lg font-semibold text-slate-900">
            {requesting ? "Request new widget" : "Add widget"}
          </h2>
        </CardHeader>
        <CardBody className="space-y-4">
          {requestSentId != null ? (
            <p className="text-sm text-slate-800">
              <Link
                to={`/personal/tickets/${encodeURIComponent(requestSentId)}`}
                className="font-medium text-brand-700 hover:underline"
                onClick={() => {
                  resetNavigationTrail();
                  onClose();
                }}
              >
                Opened support ticket {requestSentId}
              </Link>
            </p>
          ) : requesting ? (
            <>
              <label className="block space-y-2 text-sm text-slate-800">
                <span className="font-medium">What should the widget show?</span>
                <textarea
                  id="home-dashboard-request-widget"
                  className="min-h-36 w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900"
                  value={requestText}
                  onChange={(event) => setRequestText(event.target.value)}
                  maxLength={20000}
                />
              </label>
              {requestError != null ? <p className="text-sm text-rose-700">{requestError}</p> : null}
            </>
          ) : (
            <>
          <SearchableSelect
            id="home-dashboard-add-widget-modal"
            value={widgetKey}
            onValueChange={(option) => {
              const value = option?.value ?? "";
              setWidgetKey(value);
              setRequesting(false);
              const next = catalog.find((candidate) => candidate.key === value);
              if (next != null) {
                setPresentation(next.defaultPresentation);
              }
            }}
            options={catalog.map((candidate) => ({
              value: candidate.key,
              label: candidate.title,
              description: candidate.description,
            }))}
            searchValue={widgetSearch}
            onSearchChange={setWidgetSearch}
            placeholder="Choose a widget…"
            searchPlaceholder="Search widgets"
            emptyMessage="No widgets match."
          />
          {entry != null && entry.presentations.length > 1 ? (
            <HubTouchSelect
              id="home-dashboard-add-widget-presentation"
              value={presentation}
              onChange={(value) => setPresentation(value as HomeDashboardPresentation)}
              options={entry.presentations.map((value) => ({
                value,
                label: presentationLabel(value),
              }))}
              size="default"
              aria-label="Presentation"
            />
          ) : null}
          {entry?.key === "support_tickets" ? (
            <SupportTicketWidgetOptionsFields
              idPrefix="home-dashboard-add-ticket-status"
              value={ticketOptions}
              onChange={setTicketOptions}
            />
          ) : null}
          {entry != null ? <WidgetPreview entry={entry} presentation={presentation} /> : null}
            </>
          )}
          <div className="flex flex-wrap items-center justify-between gap-2">
            {requestSentId != null ? (
              <span />
            ) : (
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                if (requesting) {
                  setRequesting(false);
                  setRequestError(null);
                  return;
                }
                setRequesting(true);
                setWidgetKey("");
                setRequestError(null);
              }}
            >
              {requesting ? "Back" : "Request new widget"}
            </Button>
            )}
            <div className="flex gap-2">
            {requestSentId != null ? (
              <Button type="button" onClick={onClose}>
                Done
              </Button>
            ) : (
            <>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            {requesting ? (
              <Button
                type="button"
                disabled={requestBusy || requestText.trim() === "" || requestSentId != null}
                onClick={() => {
                  void submitWidgetRequest();
                }}
              >
                Submit
              </Button>
            ) : (
              <Button
                type="button"
                disabled={entry == null || busy}
                onClick={() => {
                  if (entry == null) {
                    return;
                  }
                  onAdd(entry.key, presentation, ticketOptions);
                  setWidgetKey("");
                  setPresentation("count");
                  setTicketOptions({
                    ticketStatuses: [],
                    ticketQueue: "active",
                    ticketPendingFeedback: false,
                  });
                }}
              >
                Add to home
              </Button>
            )}
            </>
            )}
            </div>
          </div>
        </CardBody>
      </Card>
    </div>,
    document.body,
  );
}

function TemplatesModal({
  open,
  templates,
  templateName,
  selectedTemplateId,
  busy,
  onTemplateNameChange,
  onSelectedTemplateIdChange,
  onClose,
  onSave,
  onApply,
  onUpdate,
}: {
  open: boolean;
  templates: {
    id: string;
    name: string;
    ownedByMe: boolean;
    ownerDisplayName: string | null;
  }[];
  templateName: string;
  selectedTemplateId: string;
  busy: boolean;
  onTemplateNameChange: (value: string) => void;
  onSelectedTemplateIdChange: (value: string) => void;
  onClose: () => void;
  onSave: () => void;
  onApply: () => void;
  onUpdate: () => void;
}) {
  if (!open) {
    return null;
  }
  const selectedOwned = templates.some(
    (template) => template.id === selectedTemplateId && template.ownedByMe,
  );

  return createPortal(
    <div {...hubModalOverlayProps(true)}>
      <Card className="my-8 w-full max-w-lg shadow-xl" onClick={(event) => event.stopPropagation()}>
        <CardHeader>
          <h2 className="text-lg font-semibold text-slate-900">Templates</h2>
          <p className="mt-1 text-sm text-slate-600">
            Save your layout for others, apply a shared template as a copy, or update a template you own.
          </p>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <input
              type="text"
              value={templateName}
              onChange={(event) => onTemplateNameChange(event.target.value)}
              placeholder="New template name"
              className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900"
            />
            <Button type="button" size="sm" disabled={templateName.trim() === "" || busy} onClick={onSave}>
              Save as template
            </Button>
          </div>
          <HubTouchSelect
            id="home-dashboard-template-modal"
            value={selectedTemplateId}
            onChange={onSelectedTemplateIdChange}
            options={[
              { value: "", label: "Choose a template…" },
              ...templates.map((template) => ({
                value: template.id,
                label: `${template.name}${template.ownedByMe ? " (yours)" : template.ownerDisplayName != null ? ` (${template.ownerDisplayName})` : ""}`,
              })),
            ]}
            size="default"
            aria-label="Shared template"
          />
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={selectedTemplateId === "" || busy || !selectedOwned}
              onClick={onUpdate}
            >
              Update my template
            </Button>
            <Button type="button" disabled={selectedTemplateId === "" || busy} onClick={onApply}>
              Apply to my page
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>,
    document.body,
  );
}

function supportTicketFilterHint(widget: HomeDashboardLayoutWidget): string {
  const queue = widget.ticketQueue ?? "active";
  const queueLabel =
    queue === "archived" ? "Archived" : queue === "any" ? "Active and archived" : "Active queue";
  const statuses = widget.ticketStatuses ?? [];
  const statusLabel =
    statuses.length > 0
      ? statuses.map((status) => formatHubSupportTicketStatusLabel(status)).join(", ")
      : "every status";
  const pending = widget.ticketPendingFeedback === true ? " Pending feedback only." : "";
  return `${queueLabel}. Statuses: ${statusLabel}.${pending}`;
}

function PresentationMenu({
  menuId,
  presentations,
  value,
  onChange,
  ticketOptions,
  onTicketOptionsChange,
}: {
  menuId: string;
  presentations: HomeDashboardPresentation[];
  value: HomeDashboardPresentation;
  onChange: (presentation: HomeDashboardPresentation) => void;
  ticketOptions?: SupportTicketWidgetOptions | null;
  onTicketOptionsChange?: (options: SupportTicketWidgetOptions) => void;
}) {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const [box, setBox] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!open || buttonRef.current == null) {
      setBox(null);
      return;
    }
    const rect = buttonRef.current.getBoundingClientRect();
    setBox({ top: rect.bottom + 4, left: rect.left });
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (buttonRef.current?.contains(target)) {
        return;
      }
      const menu = document.getElementById(menuId);
      if (menu?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, menuId]);

  if (presentations.length <= 1 && ticketOptions == null) {
    return null;
  }

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="absolute -left-2.5 -top-2.5 z-30 inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-600 shadow-sm hover:border-brand-500 hover:text-brand-700"
        aria-label="Change widget presentation"
        aria-haspopup="menu"
        aria-expanded={open}
        onPointerDown={(event) => event.stopPropagation()}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((current) => !current);
        }}
      >
        <ChevronDown className="h-4 w-4" />
      </button>
      {open && box != null
        ? createPortal(
            <div
              id={menuId}
              role="menu"
              className="max-h-[70vh] min-w-[16rem] overflow-y-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg"
              style={{ position: "fixed", top: box.top, left: box.left, zIndex: FLOATING_POPOVER_Z_INDEX }}
            >
              {presentations.length > 1
                ? presentations.map((presentation) => (
                <button
                  key={presentation}
                  type="button"
                  role="menuitem"
                  className={cn(
                    "flex w-full px-3 py-2 text-left text-sm hover:bg-slate-50",
                    presentation === value ? "font-medium text-brand-700" : "text-slate-800",
                  )}
                  onClick={() => {
                    onChange(presentation);
                    setOpen(false);
                  }}
                >
                  {presentationLabel(presentation)}
                </button>
              ))
                : null}
              {ticketOptions != null && onTicketOptionsChange != null ? (
                <div className="border-t border-slate-200 px-3 py-2">
                  <SupportTicketWidgetOptionsFields
                    idPrefix={`${menuId}-ticket`}
                    value={ticketOptions}
                    onChange={onTicketOptionsChange}
                  />
                </div>
              ) : null}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function WidgetCard({
  layoutWidget,
  payload,
  catalogEntry,
  favoriteOptions,
  editing,
  isDragging,
  isDragOver,
  slotWidth,
  slotHeight,
  onChangePresentation,
  onResize,
  onRemove,
  onFavoritePathsChange,
  onTicketOptionsChange,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}: {
  layoutWidget: HomeDashboardLayoutWidget;
  payload: HomeDashboardWidgetPayload | undefined;
  catalogEntry: HomeDashboardCatalogWidget | undefined;
  favoriteOptions: { value: string; label: string }[];
  editing: boolean;
  isDragging: boolean;
  isDragOver: boolean;
  slotWidth: number;
  slotHeight: number;
  onChangePresentation: (presentation: HomeDashboardPresentation) => void;
  onResize: (columnSpan: HomeDashboardColumnSpan, rowSpan: HomeDashboardRowSpan) => void;
  onRemove: () => void;
  onFavoritePathsChange: (paths: string[]) => void;
  onTicketOptionsChange: (options: SupportTicketWidgetOptions) => void;
  onDragStart: (event: DragEvent<HTMLElement>) => void;
  onDragOver: (event: DragEvent<HTMLElement>) => void;
  onDrop: (event: DragEvent<HTMLElement>) => void;
  onDragEnd: () => void;
}) {
  const { resetNavigationTrail } = useNavigationTrail();
  const [liveColumnSpan, setLiveColumnSpan] = useState<HomeDashboardColumnSpan | null>(null);
  const [liveRowSpan, setLiveRowSpan] = useState<HomeDashboardRowSpan | null>(null);
  const title =
    layoutWidget.widgetKey === "support_tickets"
      ? (catalogEntry?.title ?? "Support tickets")
      : (payload?.title ?? catalogEntry?.title ?? layoutWidget.widgetKey);
  const linkPath = payload?.linkPath ?? catalogEntry?.linkPath ?? "/";
  const presentations = catalogEntry?.presentations ?? [layoutWidget.presentation];
  const columnSpan = liveColumnSpan ?? normalizeColumnSpan(layoutWidget.columnSpan);
  const rowSpan = liveRowSpan ?? normalizeRowSpan(layoutWidget.rowSpan);
  const col = normalizeCol(layoutWidget.col);
  const row = normalizeRow(layoutWidget.row);

  function beginWidthResize(event: ReactPointerEvent<HTMLButtonElement>) {
    if (!editing || slotWidth <= 0) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const startSpan = columnSpan;
    const onMove = (moveEvent: PointerEvent) => {
      setLiveColumnSpan(
        spanFromPointerDelta(startSpan, moveEvent.clientX - startX, slotWidth + GRID_GAP_PX, GRID_COLUMNS) as HomeDashboardColumnSpan,
      );
    };
    const onUp = (upEvent: PointerEvent) => {
      const next = spanFromPointerDelta(
        startSpan,
        upEvent.clientX - startX,
        slotWidth + GRID_GAP_PX,
        GRID_COLUMNS,
      ) as HomeDashboardColumnSpan;
      // Commit first so the layout cache already has the new size before live preview clears.
      onResize(next, rowSpan);
      setLiveColumnSpan(null);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }

  function beginHeightResize(event: ReactPointerEvent<HTMLButtonElement>) {
    if (!editing || slotHeight <= 0) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const startY = event.clientY;
    const startSpan = rowSpan;
    const onMove = (moveEvent: PointerEvent) => {
      setLiveRowSpan(
        spanFromPointerDelta(startSpan, moveEvent.clientY - startY, slotHeight + GRID_GAP_PX, MAX_ROW_SPAN) as HomeDashboardRowSpan,
      );
    };
    const onUp = (upEvent: PointerEvent) => {
      const next = spanFromPointerDelta(
        startSpan,
        upEvent.clientY - startY,
        slotHeight + GRID_GAP_PX,
        MAX_ROW_SPAN,
      ) as HomeDashboardRowSpan;
      onResize(columnSpan, next);
      setLiveRowSpan(null);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }

  return (
    <div
      className={cn("relative min-h-0 min-w-0", editing && "cursor-grab active:cursor-grabbing")}
      style={{
        gridColumn: `${col + 1} / span ${columnSpan}`,
        gridRow: `${row + 1} / span ${rowSpan}`,
      }}
      draggable={editing}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
    >
      {editing ? (
        <>
          <PresentationMenu
            menuId={`home-widget-presentation-menu-${layoutWidget.instanceId}`}
            presentations={presentations}
            value={layoutWidget.presentation}
            onChange={onChangePresentation}
            ticketOptions={
              layoutWidget.widgetKey === "support_tickets"
                ? {
                    ticketStatuses: layoutWidget.ticketStatuses ?? [],
                    ticketQueue: layoutWidget.ticketQueue ?? "active",
                    ticketPendingFeedback: layoutWidget.ticketPendingFeedback === true,
                  }
                : null
            }
            onTicketOptionsChange={onTicketOptionsChange}
          />
          <button
            type="button"
            className="absolute -right-2.5 -top-2.5 z-30 inline-flex h-7 w-7 items-center justify-center rounded-full border border-rose-300 bg-white text-rose-700 shadow-sm hover:border-rose-500 hover:bg-rose-50"
            aria-label="Remove widget"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              onRemove();
            }}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </>
      ) : null}
      <Card
        className={cn(
          "flex h-full min-h-0 flex-col overflow-hidden",
          isDragging && "opacity-40",
          isDragOver && editing && "ring-2 ring-brand-500",
          editing && "ring-1 ring-slate-200",
        )}
      >
        <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0 pb-2 pr-3 pt-3">
          <CardTitleWithHelp
            hoverHint={
              layoutWidget.widgetKey === "support_tickets"
                ? `${catalogEntry?.description ?? "Support tickets."} ${supportTicketFilterHint(layoutWidget)}`
                : (catalogEntry?.description ?? "Dashboard widget.")
            }
            titleClassName="text-base"
          >
            {layoutWidget.widgetKey === "favorite_links" || editing ? (
              title
            ) : (
              <Link to={linkPath} className="text-brand-700 hover:underline" onClick={resetNavigationTrail}>
                {title}
              </Link>
            )}
          </CardTitleWithHelp>
        </CardHeader>
        <CardBody className="flex min-h-0 flex-1 flex-col overflow-y-auto pt-0">
          {payload?.error != null ? (
            <p className="text-sm text-rose-700">{payload.error}</p>
          ) : layoutWidget.presentation === "count" ? (
            <div className="flex min-h-0 flex-1 items-center justify-center px-2 py-2">
              <p className="text-center text-5xl font-semibold tabular-nums leading-none text-slate-900 md:text-6xl">
                {formatWidgetCount(layoutWidget.widgetKey, payload?.count)}
              </p>
            </div>
          ) : layoutWidget.presentation === "list" ? (
            <ul className="space-y-2">
              {(payload?.list ?? []).length === 0 ? (
                <li className="text-sm text-slate-500">Nothing waiting.</li>
              ) : (
                (payload?.list ?? []).map((item) => (
                  <li key={item.id} className="text-sm">
                    {item.href != null && !editing ? (
                      <Link
                        to={item.href}
                        className="font-medium text-brand-700 hover:underline"
                        onClick={resetNavigationTrail}
                        draggable={false}
                      >
                        {item.label}
                      </Link>
                    ) : (
                      <span className="font-medium text-slate-900">{item.label}</span>
                    )}
                    {item.sublabel != null && item.sublabel !== "" ? (
                      <span className="ml-2 text-slate-500">{item.sublabel}</span>
                    ) : null}
                  </li>
                ))
              )}
            </ul>
          ) : layoutWidget.presentation === "chart" ? (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                Total{" "}
                <span className="font-semibold tabular-nums text-slate-900">{formatWidgetCount(layoutWidget.widgetKey, payload?.count ?? 0)}</span>
              </p>
              <BarChart series={payload?.series ?? []} />
            </div>
          ) : (
            <div className="space-y-3">
              <ul className="space-y-2">
                {(layoutWidget.favoritePaths ?? []).length === 0 ? (
                  <li className="text-sm text-slate-500">
                    {editing ? "Add favorite pages below." : "No favorite links yet."}
                  </li>
                ) : (
                  (payload?.favoriteLinks ?? []).map((link) => (
                    <li key={link.path}>
                      {editing ? (
                        <span className="text-sm font-medium text-slate-900">{link.label}</span>
                      ) : (
                        <Link
                          to={link.path}
                          className="text-sm font-medium text-brand-700 hover:underline"
                          onClick={resetNavigationTrail}
                          draggable={false}
                        >
                          {link.label}
                        </Link>
                      )}
                    </li>
                  ))
                )}
              </ul>
              {editing ? (
                <>
                  <HubTouchSelect
                    id={`home-widget-favorite-add-${layoutWidget.instanceId}`}
                    value=""
                    onChange={(path) => {
                      if (path === "") {
                        return;
                      }
                      const existing = layoutWidget.favoritePaths ?? [];
                      if (existing.includes(path)) {
                        return;
                      }
                      onFavoritePathsChange([...existing, path]);
                    }}
                    options={[{ value: "", label: "Add a favorite page…" }, ...favoriteOptions]}
                    size="compact"
                    aria-label="Add favorite link"
                  />
                  {(layoutWidget.favoritePaths ?? []).length > 0 ? (
                    <ul className="space-y-1">
                      {(layoutWidget.favoritePaths ?? []).map((path) => (
                        <li key={path} className="flex items-center justify-between gap-2 text-xs text-slate-600">
                          <span className="truncate">{path}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 shrink-0 px-2"
                            onPointerDown={(event) => event.stopPropagation()}
                            onClick={() =>
                              onFavoritePathsChange((layoutWidget.favoritePaths ?? []).filter((entry) => entry !== path))
                            }
                          >
                            Remove
                          </Button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </>
              ) : null}
            </div>
          )}
        </CardBody>
      </Card>
      {editing ? (
        <>
          <button
            type="button"
            className="absolute inset-y-3 -right-1.5 z-10 w-3 cursor-ew-resize rounded-full border border-slate-300 bg-white shadow-sm hover:border-brand-500 hover:bg-brand-50"
            aria-label="Drag to resize widget width"
            onPointerDown={beginWidthResize}
            draggable={false}
          />
          <button
            type="button"
            className="absolute -bottom-1.5 left-3 right-3 z-10 h-3 cursor-ns-resize rounded-full border border-slate-300 bg-white shadow-sm hover:border-brand-500 hover:bg-brand-50"
            aria-label="Drag to resize widget height"
            onPointerDown={beginHeightResize}
            draggable={false}
          />
        </>
      ) : null}
    </div>
  );
}

export function HomeDashboardPage() {
  const queryClient = useQueryClient();
  const { data: me } = useIntegratorMe();
  const [editing, setEditing] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [templatesModalOpen, setTemplatesModalOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [draggedInstanceId, setDraggedInstanceId] = useState<string | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const [slotWidth, setSlotWidth] = useState(0);
  const [slotHeight, setSlotHeight] = useState(0);

  const catalogQuery = useQuery({
    queryKey: CATALOG_QUERY_KEY,
    queryFn: fetchHomeDashboardCatalog,
  });
  const layoutQuery = useQuery({
    queryKey: LAYOUT_QUERY_KEY,
    queryFn: fetchHomeDashboardLayout,
  });
  const widgetsQuery = useQuery({
    queryKey: WIDGETS_QUERY_KEY,
    queryFn: fetchHomeDashboardWidgets,
  });
  const templatesQuery = useQuery({
    queryKey: TEMPLATES_QUERY_KEY,
    queryFn: fetchHomeDashboardTemplates,
  });

  const saveMutation = useMutation({
    mutationFn: saveHomeDashboardLayout,
    onSuccess: (result) => {
      queryClient.setQueryData(LAYOUT_QUERY_KEY, result);
    },
  });

  const createTemplateMutation = useMutation({
    mutationFn: (name: string) => createHomeDashboardTemplate(name, layoutQuery.data?.widgets),
    onSuccess: async () => {
      setTemplateName("");
      setStatusMessage("Template saved for the organization.");
      await queryClient.invalidateQueries({ queryKey: TEMPLATES_QUERY_KEY });
    },
  });

  const updateTemplateMutation = useMutation({
    mutationFn: (id: string) =>
      updateHomeDashboardTemplate(id, { widgets: layoutQuery.data?.widgets ?? [] }),
    onSuccess: async () => {
      setStatusMessage("Template updated from your current layout.");
      await queryClient.invalidateQueries({ queryKey: TEMPLATES_QUERY_KEY });
    },
  });

  const applyTemplateMutation = useMutation({
    mutationFn: applyHomeDashboardTemplate,
    onSuccess: async (result) => {
      queryClient.setQueryData(LAYOUT_QUERY_KEY, result);
      setStatusMessage("Template applied to your home page.");
      setTemplatesModalOpen(false);
      await queryClient.invalidateQueries({ queryKey: WIDGETS_QUERY_KEY });
    },
  });

  const catalogByKey = useMemo(() => {
    const map = new Map<string, HomeDashboardCatalogWidget>();
    for (const entry of catalogQuery.data?.widgets ?? []) {
      map.set(entry.key, entry);
    }
    return map;
  }, [catalogQuery.data?.widgets]);

  const payloadByInstance = useMemo(() => {
    const map = new Map<string, HomeDashboardWidgetPayload>();
    for (const payload of widgetsQuery.data?.widgets ?? []) {
      map.set(payload.instanceId, payload);
    }
    return map;
  }, [widgetsQuery.data?.widgets]);

  const favoriteOptions = useMemo(() => {
    const moduleKeys = me?.user != null ? allowedModuleKeysFromMe(me.user) : new Set<string>();
    const options: { value: string; label: string }[] = [];
    const seen = new Set<string>();
    for (const [moduleKey, items] of Object.entries(NAV_BY_APP) as [string, NavItem[]][]) {
      if (!moduleKeys.has(moduleKey) && moduleKey !== "personal") {
        continue;
      }
      for (const item of items) {
        if (seen.has(item.to)) {
          continue;
        }
        seen.add(item.to);
        options.push({ value: item.to, label: `${item.label} (${item.to})` });
      }
    }
    options.sort((left, right) => left.label.localeCompare(right.label));
    return options;
  }, [me]);

  const layoutWidgets = layoutQuery.data?.widgets ?? [];
  const emptySlots = useMemo(
    () => (editing ? listEmptySlots(layoutWidgets, 2, EDIT_EMPTY_MINIMUM_ROWS) : []),
    [editing, layoutWidgets],
  );
  const templateBusy =
    createTemplateMutation.isPending ||
    updateTemplateMutation.isPending ||
    applyTemplateMutation.isPending;

  useLayoutEffect(() => {
    const element = gridRef.current;
    if (element == null) {
      return;
    }
    const updateMetrics = () => {
      const width = element.getBoundingClientRect().width;
      setSlotWidth(Math.max(0, (width - GRID_GAP_PX * (GRID_COLUMNS - 1)) / GRID_COLUMNS));
      const sample = element.querySelector("[data-home-slot-height]");
      if (sample instanceof HTMLElement) {
        setSlotHeight(sample.getBoundingClientRect().height);
      } else {
        setSlotHeight(element.getBoundingClientRect().height > 0 ? 168 : 0);
      }
    };
    updateMetrics();
    const observer = new ResizeObserver(updateMetrics);
    observer.observe(element);
    return () => observer.disconnect();
  }, [layoutWidgets.length, editing]);

  function layoutContentSignature(widgets: HomeDashboardLayoutWidget[]): string {
    return widgets
      .map(
        (widget) =>
          `${widget.instanceId}:${widget.widgetKey}:${widget.presentation}:${(widget.favoritePaths ?? []).join(",")}:${(widget.ticketStatuses ?? []).join(",")}:${widget.ticketQueue ?? ""}:${widget.ticketPendingFeedback === true ? "1" : "0"}`,
      )
      .join("|");
  }

  async function persist(next: HomeDashboardLayoutWidget[]) {
    setStatusMessage(null);
    const previous = layoutQuery.data?.widgets ?? [];
    const normalized = next.map((widget, index) => ({
      ...widget,
      order: index,
      columnSpan: normalizeColumnSpan(widget.columnSpan),
      rowSpan: normalizeRowSpan(widget.rowSpan),
      col: normalizeCol(widget.col),
      row: normalizeRow(widget.row),
    }));
    // Keep geometry on screen while the save round-trip runs (avoids resize flicker).
    queryClient.setQueryData(LAYOUT_QUERY_KEY, { widgets: normalized });
    try {
      const result = await saveMutation.mutateAsync(normalized);
      queryClient.setQueryData(LAYOUT_QUERY_KEY, result);
      if (layoutContentSignature(previous) !== layoutContentSignature(result.widgets)) {
        await queryClient.invalidateQueries({ queryKey: WIDGETS_QUERY_KEY });
      }
    } catch (error) {
      await queryClient.invalidateQueries({ queryKey: LAYOUT_QUERY_KEY });
      throw error;
    }
  }

  function moveWidgetToCell(instanceId: string, col: number, row: number) {
    const source = layoutWidgets.find((widget) => widget.instanceId === instanceId);
    if (source == null) {
      return;
    }
    const columnSpan = normalizeColumnSpan(source.columnSpan);
    const rowSpan = normalizeRowSpan(source.rowSpan);
    const nextCol = Math.max(0, Math.min(GRID_COLUMNS - columnSpan, col));
    const nextRow = Math.max(0, row);
    const candidate = {
      instanceId,
      col: nextCol,
      row: nextRow,
      columnSpan,
      rowSpan,
    };
    if (!placementFits(layoutWidgets, candidate)) {
      return;
    }
    void persist(
      layoutWidgets.map((widget) =>
        widget.instanceId === instanceId ? { ...widget, col: nextCol, row: nextRow } : widget,
      ),
    );
  }

  function swapWidgetPositions(sourceId: string, targetId: string) {
    const source = layoutWidgets.find((widget) => widget.instanceId === sourceId);
    const target = layoutWidgets.find((widget) => widget.instanceId === targetId);
    if (source == null || target == null) {
      return;
    }
    const next = layoutWidgets.map((widget) => {
      if (widget.instanceId === sourceId) {
        return { ...widget, col: normalizeCol(target.col), row: normalizeRow(target.row) };
      }
      if (widget.instanceId === targetId) {
        return { ...widget, col: normalizeCol(source.col), row: normalizeRow(source.row) };
      }
      return widget;
    });
    for (const widget of next) {
      const candidate = {
        instanceId: widget.instanceId,
        col: normalizeCol(widget.col),
        row: normalizeRow(widget.row),
        columnSpan: normalizeColumnSpan(widget.columnSpan),
        rowSpan: normalizeRowSpan(widget.rowSpan),
      };
      if (!placementFits(next, candidate)) {
        return;
      }
    }
    void persist(next);
  }

  function resizeWidget(
    instanceId: string,
    columnSpan: HomeDashboardColumnSpan,
    rowSpan: HomeDashboardRowSpan,
  ) {
    const source = layoutWidgets.find((widget) => widget.instanceId === instanceId);
    if (source == null) {
      return;
    }
    const col = normalizeCol(source.col);
    const row = normalizeRow(source.row);
    const clampedColumnSpan = normalizeColumnSpan(Math.min(columnSpan, GRID_COLUMNS - col));
    const next = placeWidgetPushingDownLocal(layoutWidgets, instanceId, {
      col,
      row,
      columnSpan: clampedColumnSpan,
      rowSpan,
    });
    if (next == null) {
      return;
    }
    void persist(next);
  }

  async function addWidget(
    widgetKey: HomeDashboardWidgetKey,
    presentation: HomeDashboardPresentation,
    ticketOptions: SupportTicketWidgetOptions,
  ) {
    const entry = catalogByKey.get(widgetKey);
    if (entry == null) {
      return;
    }
    const spot = findFirstOpenSlot(layoutWidgets, 1, 1);
    const next: HomeDashboardLayoutWidget[] = [
      ...layoutWidgets,
      {
        instanceId: newInstanceId(),
        widgetKey: entry.key,
        presentation,
        order: layoutWidgets.length,
        columnSpan: 1,
        rowSpan: 1,
        col: spot.col,
        row: spot.row,
        favoritePaths: entry.key === "favorite_links" ? [] : undefined,
        ticketStatuses: entry.key === "support_tickets" ? ticketOptions.ticketStatuses : undefined,
        ticketQueue: entry.key === "support_tickets" ? ticketOptions.ticketQueue : undefined,
        ticketPendingFeedback:
          entry.key === "support_tickets" ? ticketOptions.ticketPendingFeedback : undefined,
      },
    ];
    await persist(next);
    setAddModalOpen(false);
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <PageTitleWithHelp hoverHint="Your personal home dashboard. Use Edit layout to add widgets, rearrange them, delete widgets, or manage shared templates.">
          Home
        </PageTitleWithHelp>
        <div className="flex flex-wrap items-center gap-2">
          {editing ? (
            <>
              <Button type="button" size="sm" variant="secondary" onClick={() => setTemplatesModalOpen(true)}>
                Templates
              </Button>
              <Button type="button" size="sm" onClick={() => setAddModalOpen(true)}>
                <Plus className="mr-1 h-4 w-4" />
                Add widget
              </Button>
              <Button type="button" size="sm" variant="secondary" onClick={() => setEditing(false)}>
                Done editing
              </Button>
            </>
          ) : (
            <Button type="button" size="sm" variant="secondary" onClick={() => setEditing(true)}>
              <Pencil className="mr-1 h-4 w-4" />
              Edit layout
            </Button>
          )}
        </div>
      </header>

      {statusMessage != null ? <p className="text-sm text-emerald-700">{statusMessage}</p> : null}
      {saveMutation.isError ? (
        <p className="text-sm text-rose-700">Could not save your layout. Try again.</p>
      ) : null}

      {layoutWidgets.length === 0 && layoutQuery.isPending ? (
        <Card>
          <CardBody className="py-8 text-center">
            <p className="text-sm text-slate-600">Loading your home layout…</p>
          </CardBody>
        </Card>
      ) : layoutQuery.isError ? (
        <Card>
          <CardBody className="space-y-3 py-8 text-center">
            <p className="text-sm font-medium text-slate-900">Could not load your home layout</p>
            <p className="text-sm text-slate-600">Refresh the page, or try Edit layout again in a moment.</p>
            <Button type="button" size="sm" variant="secondary" onClick={() => void layoutQuery.refetch()}>
              Retry
            </Button>
          </CardBody>
        </Card>
      ) : layoutWidgets.length === 0 && !editing ? (
        <Card>
          <CardBody className="space-y-3 py-8 text-center">
            <p className="text-sm font-medium text-slate-900">Your home page is empty</p>
            <p className="text-sm text-slate-600">Turn on Edit layout to add widgets or apply a template.</p>
            <Button type="button" size="sm" onClick={() => setEditing(true)}>
              <Pencil className="mr-1 h-4 w-4" />
              Edit layout
            </Button>
          </CardBody>
        </Card>
      ) : (
        <div
          ref={gridRef}
          className="grid grid-cols-1 gap-4 md:grid-cols-4"
          style={{ gridAutoRows: SLOT_ROW_HEIGHT }}
        >
          <div data-home-slot-height className="pointer-events-none absolute h-[10.5rem] w-0 opacity-0" aria-hidden />
          {editing
            ? emptySlots.map((slot) => {
                const key = `slot-${slot.col}-${slot.row}`;
                return (
                  <div
                    key={key}
                    className={cn(
                      "hidden min-h-0 rounded-md border border-dashed border-slate-300 bg-slate-50/70 md:block",
                      dragOverKey === key && "border-brand-500 bg-brand-50/80",
                    )}
                    style={{
                      gridColumn: `${slot.col + 1} / span 1`,
                      gridRow: `${slot.row + 1} / span 1`,
                    }}
                    onDragOver={(event) => {
                      if (draggedInstanceId == null) {
                        return;
                      }
                      event.preventDefault();
                      event.dataTransfer.dropEffect = "move";
                      setDragOverKey(key);
                    }}
                    onDrop={(event) => {
                      event.preventDefault();
                      const sourceId = draggedInstanceId ?? event.dataTransfer.getData("text/plain");
                      if (sourceId.trim() !== "") {
                        moveWidgetToCell(sourceId, slot.col, slot.row);
                      }
                      setDraggedInstanceId(null);
                      setDragOverKey(null);
                    }}
                    onDragLeave={() => {
                      setDragOverKey((current) => (current === key ? null : current));
                    }}
                  />
                );
              })
            : null}
          {layoutWidgets.map((widget) => (
            <WidgetCard
              key={widget.instanceId}
              layoutWidget={widget}
              payload={payloadByInstance.get(widget.instanceId)}
              catalogEntry={catalogByKey.get(widget.widgetKey)}
              favoriteOptions={favoriteOptions}
              editing={editing}
              isDragging={draggedInstanceId === widget.instanceId}
              isDragOver={dragOverKey === `widget-${widget.instanceId}`}
              slotWidth={slotWidth}
              slotHeight={slotHeight}
              onChangePresentation={(presentation) => {
                const next = layoutWidgets.map((entry) =>
                  entry.instanceId === widget.instanceId ? { ...entry, presentation } : entry,
                );
                void persist(next);
              }}
              onResize={(columnSpan, rowSpan) => {
                resizeWidget(widget.instanceId, columnSpan, rowSpan);
              }}
              onRemove={() => {
                void persist(layoutWidgets.filter((entry) => entry.instanceId !== widget.instanceId));
              }}
              onFavoritePathsChange={(paths) => {
                const next = layoutWidgets.map((entry) =>
                  entry.instanceId === widget.instanceId ? { ...entry, favoritePaths: paths } : entry,
                );
                void persist(next);
              }}
              onTicketOptionsChange={(options) => {
                const next = layoutWidgets.map((entry) =>
                  entry.instanceId === widget.instanceId ? { ...entry, ...options } : entry,
                );
                void persist(next);
              }}
              onDragStart={(event) => {
                if (!editing) {
                  return;
                }
                const target = event.target;
                if (target instanceof Element && target.closest("button, a, input, select, textarea, [role='menu']")) {
                  event.preventDefault();
                  return;
                }
                setDraggedInstanceId(widget.instanceId);
                event.dataTransfer.effectAllowed = "move";
                event.dataTransfer.setData("text/plain", widget.instanceId);
              }}
              onDragOver={(event) => {
                if (!editing || draggedInstanceId == null || draggedInstanceId === widget.instanceId) {
                  return;
                }
                event.preventDefault();
                event.dataTransfer.dropEffect = "move";
                setDragOverKey(`widget-${widget.instanceId}`);
              }}
              onDrop={(event) => {
                if (!editing) {
                  return;
                }
                event.preventDefault();
                const sourceId = draggedInstanceId ?? event.dataTransfer.getData("text/plain");
                if (sourceId.trim() !== "" && sourceId !== widget.instanceId) {
                  swapWidgetPositions(sourceId, widget.instanceId);
                }
                setDraggedInstanceId(null);
                setDragOverKey(null);
              }}
              onDragEnd={() => {
                setDraggedInstanceId(null);
                setDragOverKey(null);
              }}
            />
          ))}
        </div>
      )}

      <AddWidgetModal
        open={addModalOpen}
        catalog={catalogQuery.data?.widgets ?? []}
        busy={saveMutation.isPending}
        onClose={() => setAddModalOpen(false)}
        onAdd={(widgetKey, presentation, ticketOptions) => {
          void addWidget(widgetKey, presentation, ticketOptions);
        }}
      />

      <TemplatesModal
        open={templatesModalOpen}
        templates={templatesQuery.data?.templates ?? []}
        templateName={templateName}
        selectedTemplateId={selectedTemplateId}
        busy={templateBusy}
        onTemplateNameChange={setTemplateName}
        onSelectedTemplateIdChange={setSelectedTemplateId}
        onClose={() => setTemplatesModalOpen(false)}
        onSave={() => void createTemplateMutation.mutateAsync(templateName.trim())}
        onApply={() => void applyTemplateMutation.mutateAsync(selectedTemplateId)}
        onUpdate={() => void updateTemplateMutation.mutateAsync(selectedTemplateId)}
      />
    </div>
  );
}
